import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../../router/all_routes";
import CollapseHeader from "../../../../../core/common/collapse-header";
import { checkPermission } from "../../../../../core/permissons/CheckPermissons";
import { Table, Button, Form, Modal } from "react-bootstrap";
import apiDefinitions from "../../../../../core/api/apiDefinitions";
import {
  showSuccessAlert,
  showError,
} from "../../../../../core/alert/sweetAlertConfig";

const route = all_routes;

interface User {
  empNumber: string;
  name: string;
  status: string;
  userType?: string;
}

interface AssignApproverForm {
  empNumber: string;
  approverType: string;
}

const AddKeyApprover: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [formData, setFormData] = useState<AssignApproverForm>({
    empNumber: "",
    approverType: "NORMAL",
  });

  const navItems = [
    {
      label: "View Users",
      icon: "ti ti-layout-dashboard",
      link: route.ViewUsers,
      permission: "ROLE_GET_USERS",
    },
    {
      label: "Add Users",
      icon: "ti ti-plus",
      link: route.AddUsers,
      permission: "ROLE_ADD_USERS",
    },
    {
      label: "Add Key Approver",
      icon: "ti ti-key",
      link: route.addkeyapprover,
      permission: "ROLE_ASSIGN_APPROVER",
    },
    {
      label: "Deactivate Key Approver",
      icon: "ti ti-x",
      link: route.deactivatekeyapprover, // Ensure this route is defined
      permission: "ROLE_ASSIGN_APPROVER",
    },
  ];

  const approverTypes = ["NORMAL", "RESTRICTED"];

  useEffect(() => {
    if (checkPermission("ROLE_ASSIGN_APPROVER")) {
      fetchUsers();
    }
  }, []);

  useEffect(() => {
    const filtered = users.filter(
      (user) =>
        (user.status === "ACTIVE" || user.status === "Active") &&
        user.empNumber.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const payload = {
        filterType: null,
        filterValue: "",
        date: null,
      };

      let page = 1;
      let hasMore = true;
      const allUsers: User[] = [];

      while (hasMore) {
        const response = await apiDefinitions.getUsers(page, payload);
        if (response.data.status === "S1000") {
          const usersPage = response.data.data.users;
          if (!usersPage || usersPage.length === 0) {
            hasMore = false;
            break;
          }

          const mappedUsers = usersPage
            .filter((user: any) => user.empNumber && (user.status === "ACTIVE" || user.status === "Active"))
            .map((user: any) => ({
              empNumber: user.empNumber.toString(),
              name: user.name || "Unknown",
              status: user.status || "PENDING",
              userType: user.group?.replace("_K", "") || "USER",
            }));

          allUsers.push(...mappedUsers);
          page++;
        } else {
          showError("Failed to fetch users: " + response.data.statusDescription);
          break;
        }
      }

      setUsers(allUsers);
    } catch (error: any) {
      showError(
        "Failed to fetch users: " + (error.response?.data?.statusDescription || "Network error")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleAssignClick = (user: User) => {
    setFormData({
      empNumber: user.empNumber,
      approverType: "NORMAL",
    });
    setShowModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAssignApprover = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.empNumber) {
      showError("Please select a user");
      return;
    }

    if (!approverTypes.includes(formData.approverType)) {
      showError("Please select a valid approver type (NORMAL or RESTRICTED)");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        empNo: formData.empNumber,
        approverType: formData.approverType,
      };
      const response = await apiDefinitions.assignKeyApprovers(payload);
      if (response.data.status === "S1000") {
        showSuccessAlert("Approver assigned successfully");
        setShowModal(false);
        setFormData({ empNumber: "", approverType: "NORMAL" });
        fetchUsers();
      } else {
        showError(`Failed to assign approver: ${response.data.statusDescription}`);
      }
    } catch (error: any) {
      showError(
        `Failed to assign approver: ${error.response?.data?.statusDescription || "Network error"}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ empNumber: "", approverType: "NORMAL" });
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Administration/Users/Assign Key Approver</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-body pb-0 pt-2">
                  <ul className="nav nav-tabs nav-tabs-bottom">
                    {navItems.map((item, index) =>
                      item.permission && checkPermission(item.permission) ? (
                        <li className="nav-item me-3" key={index}>
                          <Link
                            to={item.link}
                            className={`nav-link px-0 ${item.link === route.addkeyapprover ? "active" : ""}`}
                          >
                            <i className={item.icon}></i> {item.label}
                          </Link>
                        </li>
                      ) : null
                    )}
                  </ul>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-body">
                      <h5 className="card-title">Assign New Approver</h5>
                      <div className="mb-3">
                        <Form.Group>
                          <Form.Label>Search by Employee ID</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter Employee ID"
                            value={searchTerm}
                            onChange={handleSearchChange}
                          />
                        </Form.Group>
                      </div>
                      <div className="table-responsive">
                        <Table className="table text-nowrap table-borderless">
                          <thead>
                            <tr>
                              <th scope="col">User Name</th>
                              <th scope="col">Employee ID</th>
                              <th scope="col">User Type</th>
                              <th scope="col">Status</th>
                              <th scope="col">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr>
                                <td colSpan={5}>Loading...</td>
                              </tr>
                            ) : filteredUsers.length === 0 ? (
                              <tr>
                                <td colSpan={5}>No active users found</td>
                              </tr>
                            ) : (
                              filteredUsers.map((user) => (
                                <tr key={user.empNumber}>
                                  <th scope="row">{user.name}</th>
                                  <td>{user.empNumber}</td>
                                  <td>{user.userType}</td>
                                  <td>
                                    <span
                                      className={`badge bg-${
                                        user.status === "ACTIVE" ? "success" : "info"
                                      }`}
                                    >
                                      {user.status}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    {checkPermission("ROLE_ASSIGN_APPROVER") && (
                                      <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => handleAssignClick(user)}
                                        disabled={loading}
                                      >
                                        <i className="feather icon-key me-1"></i> Assign
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Key Approver</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAssignApprover}>
            <Form.Group className="mb-3">
              <Form.Label>Employee ID</Form.Label>
              <Form.Control
                type="text"
                name="empNumber"
                value={formData.empNumber}
                readOnly
                disabled
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Approver Type</Form.Label>
              <Form.Select
                name="approverType"
                value={formData.approverType}
                onChange={handleInputChange}
              >
                {approverTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Button variant="success" type="submit" disabled={loading}>
              {loading ? "Assigning..." : "Assign Approver"}
            </Button>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseModal} disabled={loading}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AddKeyApprover;